import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

/**
 * Keeps the "Date" / "Horaire" cart line item property inputs in sync with
 * the currently selected atelier slot (variant).
 *
 * Two complementary mechanisms are used:
 *  - A direct, synchronous update read straight from the checked slot input's
 *    `data-slot-start` attribute whenever it changes. This is the primary
 *    mechanism: it works immediately regardless of event ordering, and is
 *    timezone-safe since it reads the date/time components directly out of
 *    the ISO string (which already encodes the correct Europe/Paris local
 *    time) rather than through a JS Date object, which would otherwise
 *    convert to the visitor's own browser timezone.
 *  - A morph from the freshly re-rendered section fragment on each
 *    ProductSelectEvent, mirroring the pattern used by the theme's own
 *    <product-inventory> component, kept as a defensive fallback.
 */
class AtelierLineItemProperties extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    this.scopeRoot = closestSection;
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    closestSection?.addEventListener('change', this.#handleSlotChange);
    // Sync immediately in case the picker's checked slot no longer matches
    // what was server-rendered (e.g. a 48h-cutoff guard already switched it).
    this.#syncFromCheckedSlot();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.scopeRoot?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.scopeRoot?.removeEventListener('change', this.#handleSlotChange);
  }

  /**
   * @param {Event} event
   */
  #handleSlotChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.dataset.atelierSlot !== 'true') return;
    this.#syncFromCheckedSlot();
  };

  #syncFromCheckedSlot() {
    const picker = this.scopeRoot?.querySelector('variant-picker');
    const checked = picker?.querySelector('input[data-atelier-slot="true"]:checked');
    if (!checked) return;

    const start = checked.dataset.slotStart;
    if (!start) return;

    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(start);
    if (!match) return;
    const [, year, month, day, hour, minute] = match;

    const dateInput = this.querySelector('input[name="properties[Date]"]');
    const timeInput = this.querySelector('input[name="properties[Horaire]"]');
    if (dateInput) dateInput.value = `${day}/${month}/${year}`;
    if (timeInput) timeInput.value = `${hour}h${minute}`;
  }

  /**
   * @param {CustomEvent & { target: EventTarget, promise: Promise<any> }} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const updated = html.querySelector('atelier-line-item-properties');
        if (!updated) return;

        morph(this, updated, { childrenOnly: true });
        this.#syncFromCheckedSlot();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[atelier-line-item-properties] Event promise rejected:', error);
        }
      });
  };
}

if (!customElements.get('atelier-line-item-properties')) {
  customElements.define('atelier-line-item-properties', AtelierLineItemProperties);
}
